<template>
  <div
    v-if="
      availableDiscounts.length > 0 && variant.available && !hasCompareAtPrice
    "
    class="global-graduated-discounts"
  >
    <div class="global-graduated-discounts__header">
      <h3
        class="global-graduated-discounts__title text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
      >
        {{ $string('product.discount_options.title') }}
      </h3>
    </div>

    <div class="global-graduated-discounts__options">
      <button
        v-for="discount of availableDiscounts"
        :key="`discount-${discount.thresholdQuantity}-${discount.discountPercentage}`"
        type="button"
        class="global-graduated-discounts__option"
        :class="{
          'is-selected':
            selectedDiscount &&
            selectedDiscount.thresholdQuantity === discount.thresholdQuantity &&
            selectedDiscount.discountPercentage === discount.discountPercentage,
        }"
        @click="selectDiscount(discount)"
      >
        <div class="global-graduated-discounts__option-content">
          <div class="global-graduated-discounts__pack-info">
            <span
              class="global-graduated-discounts__quantity text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
              v-text="
                `${discount.thresholdQuantity} ${$string('product.discount_options.pack')}`
              "
            />
            <span
              v-if="discount.discountPercentage > 0"
              class="global-graduated-discounts__discount text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
              v-text="getDiscountText(discount)"
            />
          </div>
        </div>
        <div class="global-graduated-discounts__option-price">
          <span class="global-graduated-discounts__price-line">
            <span
              v-if="discount.discountPercentage > 0"
              class="global-graduated-discounts__original-price text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
            >
              {{ $formatMoney(baseVariantPrice * discount.thresholdQuantity) }}
            </span>
            <span
              class="global-graduated-discounts__discounted-price text-mobile-paragraph-s-bold text-desktop-paragraph-s-bold"
            >
              {{
                $formatMoney(
                  baseVariantPrice *
                    discount.thresholdQuantity *
                    (1 - discount.discountPercentage / 100),
                )
              }}
            </span>
          </span>
        </div>
      </button>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Global graduated discounts (global-graduated-discounts)
 * -----------------------------------------------------------------------------
 * Component to display quantity discount options for variants that are part of
 * the global graduated discounts metafield.
 *
 * @param {Object} variant - Current selected variant
 *
 * @emits update-quantity - Emit selected quantity for the discount
 * @emits update-discount - Emit selected discount object
 */

export default {
  name: 'GlobalGraduatedDiscounts',

  props: {
    variant: {
      type: Object,
      required: true,
    },
    product: {
      type: Object,
      required: false,
      default: null,
    },
  },

  emits: {
    'update-quantity': (quantity) => typeof quantity === 'number',
    'update-discount': (discount) => typeof discount === 'object',
  },

  data() {
    return {
      selectedDiscount: null,
    };
  },

  computed: {
    /**
     * Check if the variant has a compare-at price (is on sale)
     * Products with compare-at prices are not eligible for bulk discounts
     * @returns {Boolean}
     */
    hasCompareAtPrice() {
      return Boolean(
        this.variant?.compareAtPrice?.amount ||
          this.variant?.compareAtPriceDiscount,
      );
    },

    /**
     * Get the base variant price without any activeDiscounts applied
     * This ensures graduated discounts don't stack on top of campaign discounts
     * @returns {Number}
     */
    baseVariantPrice() {
      // The variant.price.amount is the raw Shopify price
      // It doesn't include campaign discounts (those are applied in product-prices component)
      return this.variant?.price?.amount || 0;
    },

    /**
     * Get available discounts for the current variant
     * @returns {Array}
     */
    availableDiscounts() {
      const globalDiscounts = this.$variable(
        'discount.globalGraduatedDiscounts',
      );

      if (!globalDiscounts) {
        return [];
      }

      if (!globalDiscounts.isActive) {
        return [];
      }

      if (!globalDiscounts.tiers) {
        return [];
      }

      const variantId = this.variant.id.toString();

      const availableDiscounts = [];

      // Find tiers that include this variant or product's collections
      globalDiscounts.tiers.forEach((tier) => {
        // Check if variant is in specific items
        const variantInItems =
          tier.items &&
          tier.items.length > 0 &&
          tier.items.some((item) => {
            // Check both formats: direct ID and Shopify GID format
            const directMatch = item.variantIds.includes(variantId);
            const gidMatch = item.variantIds.some((id) =>
              id.includes(`ProductVariant/${variantId}`),
            );
            const isMatch = directMatch || gidMatch;

            return isMatch;
          });

        // Check if product is in any of the tier's collections
        const productInCollections =
          tier.collections &&
          tier.collections.length > 0 &&
          tier.collections.some((collection) => {
            // Get product collection IDs
            const productCollectionIds =
              this.product?.collectionIds || this.product?.collections || [];

            // Extract numeric ID from GID format (e.g., "gid://shopify/Collection/309220606070" -> "309220606070")
            const collectionNumericId = collection.id.split('/').pop();

            // Check if product belongs to this collection
            const isInCollection = productCollectionIds.some((prodColId) => {
              const prodColNumericId = prodColId.toString();
              return prodColNumericId === collectionNumericId;
            });

            return isInCollection;
          });

        const variantInTier = variantInItems || productInCollections;

        if (variantInTier) {
          const discountOption = {
            thresholdQuantity: parseInt(tier.thresholdQuantity),
            discountPercentage: parseInt(tier.discountPercentage),
            tier,
          };
          availableDiscounts.push(discountOption);
        }
      });

      // Always add a 1 Pack option if there are any discounts available
      if (availableDiscounts.length > 0) {
        // Check if 1 Pack option already exists
        const hasOnePack = availableDiscounts.some(
          (discount) => discount.thresholdQuantity === 1,
        );
        if (!hasOnePack) {
          availableDiscounts.unshift({
            thresholdQuantity: 1,
            discountPercentage: 0,
            tier: null,
          });
        }
      }

      // Sort by threshold quantity (ascending)
      const sortedDiscounts = availableDiscounts.sort(
        (a, b) => a.thresholdQuantity - b.thresholdQuantity,
      );
      return sortedDiscounts;
    },
  },

  watch: {
    /**
     * Reset selection when variant changes
     */
    variant() {
      this.clearDiscount();
    },
  },

  mounted() {
    // Auto-select 1 Pack option if available
    this.$nextTick().then(() => {
      const onePackOption = this.availableDiscounts.find(
        (discount) => discount.thresholdQuantity === 1,
      );
      if (onePackOption && !this.selectedDiscount) {
        this.selectDiscount(onePackOption);
      }
    });
  },

  methods: {
    /**
     * Select a discount option
     * @param {Object} discount - Selected discount object
     */
    selectDiscount(discount) {
      this.selectedDiscount = discount;
      this.$emit('update-quantity', discount.thresholdQuantity);
      this.$emit('update-discount', discount);

      // Force update of parent component to refresh prices
      this.$nextTick().then(() => {
        this.$forceUpdate();
      });
    },

    /**
     * Clear selected discount
     */
    clearDiscount() {
      this.selectedDiscount = null;
      this.$emit('update-quantity', 1);
      this.$emit('update-discount', null);
    },

    /**
     * Get discount text based on discount percentage
     * @param {Object} discount - Discount object
     * @returns {String}
     */
    getDiscountText(discount) {
      if (discount.discountPercentage === 0) {
        return 'No discount';
      }

      const originalPrice = this.baseVariantPrice * discount.thresholdQuantity;
      const discountedPrice =
        originalPrice * (1 - discount.discountPercentage / 100);
      const savings = originalPrice - discountedPrice;

      return `Save ${this.$formatMoney(savings)}`;
    },
  },
};
</script>

<style lang="scss">
@import './global-graduated-discounts';
</style>
